import { api, withRefresh } from "@/lib/auth";
import { ANKH_PATHS, ANKH_JEWEL } from "@/lib/ankh";

const W = 3508;
const H = 2480;
const GOLD = "#D9B47A";
const FIT = 0.65;

const spaced = (ctx, px) => {
  if ("letterSpacing" in ctx) ctx.letterSpacing = `${px}px`;
};

const text = (ctx, s, x, y, font, color, spacing = 0, align = "center") => {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  spaced(ctx, spacing);
  ctx.fillText(s, x, y);
  spaced(ctx, 0);
};

export const sigil = (ctx, x, y, scale, stroke, width) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale * FIT, scale * FIT);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = width / FIT;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ANKH_PATHS.forEach((d) => ctx.stroke(new Path2D(d)));
  ctx.fillStyle = "#C2414F";
  ctx.beginPath();
  ctx.arc(ANKH_JEWEL.cx, ANKH_JEWEL.cy, ANKH_JEWEL.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const frame = (ctx) => {
  ctx.fillStyle = "#0A0706";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H / 2, 100, W / 2, H / 2, 1700);
  glow.addColorStop(0, "rgba(217,180,122,0.10)");
  glow.addColorStop(1, "rgba(10,7,6,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 5;
  ctx.strokeRect(120, 120, W - 240, H - 240);
  ctx.lineWidth = 1.5;
  ctx.strokeRect(160, 160, W - 320, H - 320);
  [[160, 160], [W - 160, 160], [160, H - 160], [W - 160, H - 160]].forEach(([x, y]) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = "#C2414F";
    ctx.fillRect(-16, -16, 32, 32);
    ctx.restore();
  });
  ctx.save();
  ctx.globalAlpha = 0.07;
  ctx.strokeStyle = GOLD;
  [1000, 860, 560].forEach((r) => {
    ctx.beginPath();
    ctx.arc(W / 2, H / 2 + 60, r, 0, Math.PI * 2);
    ctx.lineWidth = 3;
    ctx.stroke();
  });
  for (let a = 0; a < 48; a += 1) {
    ctx.save();
    ctx.translate(W / 2, H / 2 + 60);
    ctx.rotate((a * Math.PI) / 24);
    ctx.beginPath();
    ctx.moveTo(0, -1000);
    ctx.lineTo(0, a % 4 === 0 ? -920 : -960);
    ctx.stroke();
    ctx.restore();
  }
  sigil(ctx, W / 2, H / 2 + 60, 14, GOLD, 0.5);
  ctx.restore();
};

const seal = (ctx, x, y) => {
  const g = ctx.createRadialGradient(x - 40, y - 45, 15, x, y, 130);
  g.addColorStop(0, "#F4D9A4");
  g.addColorStop(0.5, GOLD);
  g.addColorStop(1, "#8A6630");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, 115, 0, Math.PI * 2);
  ctx.fill();
  sigil(ctx, x, y, 1.85, "#3A2810", 3.5);
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, 180, 0, Math.PI * 2);
  ctx.stroke();
  const ring = "THIRTEEN GATES · MASTERY · THE TANTRIC TEMPLE · ";
  ctx.font = '500 28px "DM Mono"';
  ctx.fillStyle = GOLD;
  ctx.textAlign = "center";
  [...ring].forEach((ch, k) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((k / ring.length) * Math.PI * 2);
    ctx.fillText(ch, 0, -145);
    ctx.restore();
  });
};

const signature = (ctx, right, base) => {
  ctx.save();
  let size = 260;
  ctx.font = `400 ${size}px "Ariel Signature"`;
  while (ctx.measureText("Goddess Ariel").width > 980 && size > 120) {
    size -= 10;
    ctx.font = `400 ${size}px "Ariel Signature"`;
  }
  ctx.textAlign = "right";
  const w = ctx.measureText("Goddess Ariel").width;
  const shine = ctx.createLinearGradient(right - w, base - 160, right, base + 40);
  shine.addColorStop(0, "#B8904F");
  shine.addColorStop(0.5, "#F4D9A4");
  shine.addColorStop(1, "#C9A15F");
  ctx.fillStyle = shine;
  ctx.fillText("Goddess Ariel", right, base);
  ctx.strokeStyle = shine;
  ctx.lineCap = "round";
  const x0 = right - w - 40;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(x0, base + 34);
  ctx.bezierCurveTo(x0 + w * 0.3, base + 70, x0 + w * 0.7, base - 10, right + 30, base + 24);
  ctx.bezierCurveTo(right + 90, base + 34, right + 60, base - 80, right - 10, base - 40);
  ctx.stroke();
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(x0 + 60, base + 58);
  ctx.bezierCurveTo(x0 + w * 0.4, base + 96, x0 + w * 0.8, base + 40, right - 40, base + 62);
  ctx.stroke();
  ctx.restore();
};

let sigFont;
const loadSignatureFont = () => {
  sigFont = sigFont ?? new FontFace("Ariel Signature", "url(/fonts/mrs-saint-delafield.woff2)").load().then((f) => document.fonts.add(f)).catch(() => null);
  return sigFont;
};

export async function drawCertificate(canvas, { name, date }) {
  await loadSignatureFont();
  await Promise.all(['400 100px "Bodoni Moda"', 'italic 400 100px "Bodoni Moda"', '400 50px "DM Mono"', '300 50px "Hanken Grotesk"'].map((f) => document.fonts.load(f)));
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  frame(ctx);
  const cx = W / 2;
  sigil(ctx, cx, 420, 3.6, GOLD, 2.6);
  text(ctx, "THE TANTRIC TEMPLE", cx, 680, '500 50px "DM Mono"', GOLD, 26);
  text(ctx, "Certificate of Mastery", cx, 900, 'italic 400 190px "Bodoni Moda"', "#F2E8DF");
  text(ctx, "This certifies that", cx, 1090, '300 60px "Hanken Grotesk"', "#CFC2B6", 4);
  let size = 220;
  ctx.font = `400 ${size}px "Bodoni Moda"`;
  while (ctx.measureText(name).width > W - 900 && size > 90) {
    size -= 10;
    ctx.font = `400 ${size}px "Bodoni Moda"`;
  }
  text(ctx, name, cx, 1330, `400 ${size}px "Bodoni Moda"`, GOLD);
  ctx.fillStyle = GOLD;
  ctx.fillRect(cx - 700, 1400, 1400, 2);
  const body = ["has walked all thirteen Gates of the Orgasm Matrix — thirty-nine weeks", "of devoted practice, from Root Resonance to Embodied Enlightenment."];
  body.forEach((l, k) => text(ctx, l, cx, 1540 + k * 96, '400 68px "Bodoni Moda"', "#E4D8CC"));
  for (let k = 0; k < 13; k += 1) {
    ctx.save();
    ctx.translate(cx + (k - 6) * 70, 1800);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = k === 12 ? "#C2414F" : GOLD;
    ctx.fillRect(-9, -9, 18, 18);
    ctx.restore();
  }
  text(ctx, "Live ecstatically forever. Blessed Be.", cx, 1930, 'italic 400 64px "Bodoni Moda"', GOLD);
  const by = 2170;
  const L = 440;
  const R = W - 440;
  text(ctx, date, L, by - 30, '400 70px "Bodoni Moda"', "#F2E8DF", 0, "left");
  ctx.fillStyle = GOLD;
  ctx.fillRect(L, by + 10, 820, 2);
  text(ctx, "DATE OF MASTERY", L, by + 80, '400 34px "DM Mono"', "#A8998C", 10, "left");
  signature(ctx, R, by - 40);
  ctx.fillStyle = GOLD;
  text(ctx, "PRIESTESS OF PLEASURE", R, by + 80, '400 34px "DM Mono"', "#A8998C", 10, "right");
  seal(ctx, cx, by - 10);
}

export const masteryDate = (data) => {
  const stamps = Object.values(data ?? {}).map((e) => e?.weeks?.[2]).filter(Boolean).sort();
  const last = stamps[stamps.length - 1];
  return (last ? new Date(last) : new Date()).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

export const allMastered = (data) => Array.from({ length: 13 }, (_, i) => data?.[i]).every((e) => Array.isArray(e?.weeks) && e.weeks.filter(Boolean).length === 3);

export async function saveCertificate(canvas) {
  const image = canvas.toDataURL("image/png");
  const { data } = await withRefresh(() => api.post("/mastery/certificate", { image }));
  return data;
}

export async function celebrateMastery(name, data) {
  const canvas = document.createElement("canvas");
  await drawCertificate(canvas, { name: (name ?? "").trim() || "Beloved Initiate", date: masteryDate(data) });
  return saveCertificate(canvas);
}
